import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote, ExternalLink } from "lucide-react";
import { BUSINESS } from "@/data/business";
import { Reveal } from "./Reveal";
import { Parallax } from "./Parallax";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_STATE = { loading: true, enabled: false, reviews: [], rating: null, total: null };

/** Map the /api/reviews payload into component state. */
const toReviewsState = (data) => ({
  loading: false,
  enabled: data.enabled,
  reviews: data.reviews || [],
  rating: data.rating,
  total: data.total_reviews,
});

const Stars = ({ value = 5 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={16}
        strokeWidth={1.5}
        className={i <= Math.round(value) ? "fill-[#C89A4A] text-[#C89A4A]" : "text-[color:rgba(43,31,23,0.25)]"}
      />
    ))}
  </div>
);

const GoogleReviewsButton = () => (
  <a
    href={BUSINESS.googleReviewsUrl}
    target="_blank"
    rel="noreferrer"
    data-testid="google-reviews-button"
    className="group btn-glass-light sheen inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-[background-color,box-shadow,transform] duration-300 active:scale-[0.98]"
  >
    Read our Google Reviews
    <ExternalLink size={15} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const ReviewCard = ({ review }) => (
  <figure className="glass glass-hover h-full p-6">
    <Quote size={22} className="text-[#C89A4A]" strokeWidth={1.5} />
    <Stars value={review.rating} />
    <blockquote className="mt-3 text-sm leading-relaxed text-[color:var(--text-muted)]">
      “{review.text}”
    </blockquote>
    <figcaption className="mt-5 text-sm font-semibold text-[color:var(--text)]">
      {review.author_name}
      {review.relative_time && (
        <span className="ml-2 font-normal text-[color:var(--text-subtle)]">{review.relative_time}</span>
      )}
    </figcaption>
  </figure>
);

const ReviewsFallback = () => (
  <Reveal>
    <div className="glass p-8 sm:p-10">
      <div className="inline-flex items-center gap-3">
        <Stars value={5} />
        <span className="eyebrow">Best of 2026</span>
      </div>
      <h3 className="mt-4 font-serif text-2xl font-light tracking-tight sm:text-3xl">
        Recognized among the best facial spas in Niles
      </h3>
      <p className="mt-3 max-w-lg text-base leading-relaxed text-[color:var(--text-muted)]">
        Honored with the BusinessRate Best of 2026 award, powered by Google Reviews. Our clients
        share their glow on Google. Read real, verified reviews and see why they trust Bahra
        Skincare for radiant, healthy skin.
      </p>
      <div className="mt-7">
        <GoogleReviewsButton />
      </div>
    </div>
  </Reveal>
);

const RatingSummary = ({ rating, total }) => (
  <div className="mt-4 flex items-center justify-center gap-3">
    <Stars value={rating} />
    <span data-testid="google-rating-text" className="text-sm text-[color:var(--text-muted)]">
      {Number(rating).toFixed(1)} on Google
      {total ? ` · ${total} reviews` : ""}
    </span>
  </div>
);

const LiveReviewsGrid = ({ reviews }) => (
  <div className="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2" data-testid="reviews-carousel">
    {reviews.slice(0, 4).map((r, idx) => (
      <Reveal key={`${r.author_name || "review"}-${r.time || r.relative_time || idx}`} delay={idx * 0.06}>
        <ReviewCard review={r} />
      </Reveal>
    ))}
  </div>
);

const AwardBadgeCard = () => (
  <Reveal className="mx-auto w-full max-w-[280px] lg:max-w-none">
    <Parallax speed={0.08}>
      <div className="glass-strong glass-hover overflow-hidden p-2.5" data-testid="reviews-award-badge">
        <img
          src={BUSINESS.award.image}
          alt={BUSINESS.award.alt}
          className="w-full rounded-[14px]"
          loading="lazy"
        />
      </div>
    </Parallax>
  </Reveal>
);

export const ReviewsSection = () => {
  const [state, setState] = useState(INITIAL_STATE);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/reviews`)
      .then((res) => active && setState(toReviewsState(res.data)))
      .catch(() => active && setState((s) => ({ ...s, loading: false })));
    return () => {
      active = false;
    };
  }, []);

  const hasLive = state.enabled && state.reviews.length > 0;

  return (
    <section data-testid="reviews-section" className="section-pad">
      <div className="container-x">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">Kind Words</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
            Loved by clients across Niles
          </h2>
          {hasLive && state.rating && <RatingSummary rating={state.rating} total={state.total} />}
        </Reveal>

        <div className="mt-12 grid grid-cols-1 items-center gap-8 lg:grid-cols-[300px,1fr] lg:gap-12">
          <AwardBadgeCard />
          <div>{hasLive ? <LiveReviewsGrid reviews={state.reviews} /> : <ReviewsFallback />}</div>
        </div>

        {hasLive && (
          <div className="mt-10 flex justify-center">
            <GoogleReviewsButton />
          </div>
        )}
      </div>
    </section>
  );
};
